import { logger } from '@fiverr-private/obs';
import { LOG_LEVELS } from './Utils';
import UploadProcessManager from './UploadProcessManager/UploadProcessManager';
import PolicyValidationError from './errors/PolicyValidationError';
import RemoteActionError from './errors/RemoteActionError';
import PolicyHandler from './PolicyHandler';

class FilerrUploader {

    /**
     * @param {object} options
     * @property {string} options.objectName - S3 Aggregate name, example: 'profile'
     * @property {string} options.attachmentName - S3 Attachment name, example: 'photo'
     * @property {string} options.getPolicyUrl - Url to fetch policy data
     * @property {string} [options.createPendingAttachmentIdUrl] - Url to create pending attachment and receive an attachment Id.
     * @property {boolean} [options.shouldBeRenamed] - If true, filename will be renamed to guid
     * @property {string} [options.logLevel] - log level in case of an error, 'error' or 'warn'
     */
    constructor(options) {
        FilerrUploader.validateConstructor(options);
        this.objectName = options.objectName;
        this.attachmentName = options.attachmentName;
        this.getPolicyUrl = options.getPolicyUrl;
        this.createPendingAttachmentIdUrl = options.createPendingAttachmentIdUrl;
        this.shouldBeRenamed = options.shouldBeRenamed;
        this.policyHandler = new PolicyHandler(this.getPolicyUrl, this.objectName, this.attachmentName);
        this.logLevel = options.logLevel ? options.logLevel.toUpperCase() : LOG_LEVELS.ERROR;

        this.logError = this.logError.bind(this);
        this.upload = this.upload.bind(this);
    }

    logError(error) {
        if (this.logLevel !== LOG_LEVELS.NONE) {
            const enrichment =  {
                package: '@fiverr-private/filerr_uploader'
            };

            logger[this.logLevel](
                error,
                enrichment
            );
        }
    }

    /**
     * @param {Object} options
     * @property {File} options.file - File to upload
     * @property {onSuccessCallback} options.onSuccess - Success callback
     * @property {onErrorCallback} [options.onError = () => ({})] -  Error callback
     * @property {onProgressCallback} [options.onProgress = () => ({})] - Progress callback
     * @property {number} [options.retryAttempts = 5] - number of retries before call onError
     * @returns {UploadProcessManager} uploaderManager
     */
    upload(options) {
        try {
            FilerrUploader.validateUpload(options);
            const { file, onSuccess, onError, onProgress, retryAttempts } = options;

            const uploaderManager = new UploadProcessManager(
                this.objectName,
                this.attachmentName,
                this.createPendingAttachmentIdUrl,
                file,
                this.policyHandler,
                this.shouldBeRenamed,
                this.logLevel
            );

            const onErrorWrapper = (error) => {
                this.logError(error);
                onError(error);
            };

            uploaderManager.upload(
                onSuccess,
                onErrorWrapper,
                onProgress,
                retryAttempts
            );

            return uploaderManager;
        } catch (error) {
            this.logError(error);
            throw error;
        }
    }

    static validateConstructor(options) {
        if (!options) {
            throw new Error('Options weren\'t provided, expecting: objectName, attachmentName, getPolicyUrl, [createPendingAttachmentIdUrl]');
        }
        const initialErrorMessage = 'Some required fields were\'nt provided: ';
        const { objectName, attachmentName, getPolicyUrl, createPendingAttachmentIdUrl, logLevel } = options;
        let errorMessage = initialErrorMessage;
        if (!objectName) {
            errorMessage = `${errorMessage} objectName, `;
        }
        if (!attachmentName) {
            errorMessage = `${errorMessage} attachmentName, `;
        }
        if (!getPolicyUrl) {
            errorMessage = `${errorMessage} getPolicyUrl, `;
        }
        if (errorMessage !== initialErrorMessage) {
            throw new Error(errorMessage.slice(0, errorMessage.length - 2));
        }
        const initialTypeErrorMessage = 'should be of type: String';
        errorMessage = initialTypeErrorMessage;
        if (typeof objectName !== 'string') {
            errorMessage = ` ,objectName ${errorMessage}`;
        }
        if (typeof attachmentName !== 'string') {
            errorMessage = ` ,attachmentName ${errorMessage}`;
        }
        if (typeof getPolicyUrl !== 'string') {
            errorMessage = ` ,getPolicyUrl ${errorMessage}`;
        }
        if (createPendingAttachmentIdUrl && (typeof createPendingAttachmentIdUrl !== 'string')) {
            errorMessage = ` ,createPendingAttachmentIdUrl ${errorMessage}`;
        }
        if (errorMessage !== initialTypeErrorMessage) {
            throw new TypeError(errorMessage.slice(2));
        }
        if (logLevel && !LOG_LEVELS[logLevel.toUpperCase()]) {
            throw new TypeError(`logLevel should be one of: ${Object.values(LOG_LEVELS).join(',')}`);
        }
    }

    static validateUpload(options) {
        const initialErrorMessage = 'Some required fields were\'nt provided: ';
        const { file, onSuccess, onError, onProgress, retryAttempts } = options;
        let errorMessage = initialErrorMessage;
        if (!file) {
            errorMessage = `${errorMessage} file, `;
        }
        if (!onSuccess) {
            errorMessage = `${errorMessage} onSuccess, `;
        }
        if (errorMessage !== initialErrorMessage) {
            throw new Error(errorMessage.slice(0, errorMessage.length - 2));
        }
        if (!(file instanceof File)) {
            throw new TypeError('file should be of type: File');
        }
        const initialCallbackErrorMessage = 'should be of type: Function';
        errorMessage = initialCallbackErrorMessage;
        if (typeof onSuccess !== 'function') {
            errorMessage = ` ,onSuccess ${errorMessage}`;
        }
        if (onError && (typeof onError !== 'function')) {
            errorMessage = ` ,onError ${errorMessage}`;
        }
        if (onProgress && (typeof onProgress !== 'function')) {
            errorMessage = ` ,onProgress ${errorMessage}`;
        }
        if (errorMessage !== initialCallbackErrorMessage) {
            throw new TypeError(errorMessage.slice(2));
        }
        if (retryAttempts && typeof retryAttempts !== 'number') {
            throw new TypeError('retryAttempts should be of type: Number');
        }
    }

    /**
     * Will be called upon success
     * @callback onSuccessCallback
     * @param {Object} result
     * @property {string} result.key - s3 key
     * @property {string} result.bucketName - s3 bucket name
     * @property {string} result.etag - file's etag is a unique identifier given by AWS S3.
     * @property {Object} [result.pendingAttachmentData] - will present if createPendingAttachmentIdUrl was provided upon instantiating
     * @property {string} [result.pendingAttachmentData.pending_attachment_id]
     */

    /**
     * Will be called upon error
     * @callback onErrorCallback
     * @param {(PolicyValidationError|RemoteActionError)} err
     */

    /**
     * Will be called periodically according to the upload progress
     * @callback onProgressCallback
     * @param {Object} progressData
     * @property {ProgressEvent} progressData.progressEvent
     * @property {number} progressData.percentage
     */
}

FilerrUploader.LOG_LEVELS = LOG_LEVELS;

export {
    FilerrUploader,
    RemoteActionError,
    PolicyValidationError
};
